import { maxLength, minLength, required } from '@reliance/forms-js/lib/validation';
import { Configuration } from '@reliance/types/models/Configuration';
import { AxiosError } from 'axios';
import { FORM_ERROR } from 'final-form';
import React from 'react';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Modal from 'react-bootstrap/Modal';
import Row from 'react-bootstrap/Row';
import { Form } from 'react-final-form';
import { alertStore } from '../alert/alertStore';
import api from '../common/api';
import { IStatusResponse } from '../common/reliance-api-service';
import { TextInput } from '../form/TextInput';

type FacilityConfigurationModalProps = {
  facilityId: number;
  configuration?: Configuration;
  onClose(success?: boolean): void;
};

type FormValues = {
  key: string;
  value: string;
};

const save = async (
  data: FormValues,
  props: FacilityConfigurationModalProps
): Promise<void | { [FORM_ERROR]: string }> => {
  const isEdit = props.configuration != null && props.configuration.id != null;
  if (isEdit) {
    try {
      await api.put<IStatusResponse>(`/configuration/${props.configuration!.id}`, {
        value: data.value,
      });
    } catch (err) {
      const ax = err as AxiosError;
      if (ax.response?.status === 400) {
        return { [FORM_ERROR]: 'Please verify the inputs are valid and try again' };
      }
      return { [FORM_ERROR]: 'An unexpected error occurred' };
    }
    return;
  }
  try {
    await api.post<IStatusResponse>('/configuration', {
      key: data.key,
      value: data.value,
      facilityId: props.facilityId,
    });
  } catch (err) {
    const ax = err as AxiosError;
    if (ax.response?.status === 400) {
      return { [FORM_ERROR]: 'Please verify the inputs are valid and try again' };
    }
    if (ax.response?.status === 409) {
      return { [FORM_ERROR]: 'A configuration with this key already exists' };
    }
    return { [FORM_ERROR]: 'An unexpected error occurred' };
  }
};

const FacilityConfigurationModal: React.FC<FacilityConfigurationModalProps> = (props) => {
  const isEdit = props.configuration != null && props.configuration.id != null;

  const initialValues: FormValues = {
    key: props.configuration?.key ?? '',
    value: props.configuration?.value ?? '',
  };

  return (
    <Modal show onHide={() => props.onClose(false)}>
      <Form
        initialValues={initialValues}
        onSubmit={async (data: FormValues) => {
          const result = await save(data, props);
          if (result != null && typeof result === 'object' && FORM_ERROR in result) {
            return result;
          }
          alertStore.success(isEdit ? 'Configuration updated successfully' : 'Successfully added config', true);
          props.onClose(true);
          return undefined;
        }}
        render={({ handleSubmit, submitting, hasValidationErrors, submitError }) => (
          <form onSubmit={handleSubmit}>
            <Modal.Header closeButton>
              <Modal.Title>
                {isEdit ? 'Facility Configuration' : 'Add Facility Configuration'}
                {submitting && (
                  <>
                    {' '}
                    <i className="fas fa-spin fa-spinner" />
                  </>
                )}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {submitError != null && <Alert variant="danger">{String(submitError)}</Alert>}
              <Row>
                <Col>
                  {isEdit ? (
                    <>
                      <div className="mb-2">
                        <span className="form-label">Key</span>
                        <div>{props.configuration!.key}</div>
                      </div>
                      <TextInput
                        label="Value"
                        name="value"
                        validationRules={[required, minLength(1), maxLength(10000)]}
                      />
                    </>
                  ) : (
                    <>
                      <TextInput
                        label="Key"
                        name="key"
                        validationRules={[required, minLength(1), maxLength(500)]}
                      />
                      <TextInput
                        label="Value"
                        name="value"
                        validationRules={[required, minLength(1), maxLength(10000)]}
                      />
                    </>
                  )}
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="default" type="button" disabled={submitting} onClick={() => props.onClose(false)}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={submitting || hasValidationErrors}>
                {submitting ? (isEdit ? 'Saving...' : 'Adding...') : isEdit ? 'Save' : 'Add'}
              </Button>
            </Modal.Footer>
          </form>
        )}
      />
    </Modal>
  );
};

export default FacilityConfigurationModal;
